import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { ProviderType } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import WebhookApiPushModal from "./WebhookAPIPushModal";
import { containerClasses, textClasses, inputClasses, skeletonClasses } from "../common/tailwindClassnames";
import { page_size } from "../../api/apiUtils";

const buildLogoMap = (): Record<string, string> => {
    try {
        const context = (require as any).context('../../images/logos', false, /\.png$/);
        const map: Record<string, string> = {};
        context.keys().forEach((key: string) => {
            const id = key.replace('./', '').replace('.png', '');
            map[id] = context(key);
        });
        return map;
    } catch {
        return {};
    }
};

const logoMap = buildLogoMap();
const getProviderLogo = (id: string): string | null => logoMap[id] ?? null;


type StepOneProps = {
    providerTypes: ProviderType[],
    reloadProviderTypes: boolean,
    loading: boolean,
    fetchError?: boolean,
    onSelectProviderType: (pt: ProviderType) => void,
    onSearchProviderType: (e: React.ChangeEvent<HTMLInputElement>) => void,
}

export const getPageNumbers = (currentPage: number, totalPages: number): (number | "...")[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
    if (currentPage < 4) return [0, 1, 2, 3, 4, "...", totalPages - 1];
    if (currentPage >= totalPages - 4) return [0, "...", totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1];
    return [0, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages - 1];
};

const NewConnectionStepOne = ({
    providerTypes,
    reloadProviderTypes,
    loading,
    fetchError = false,
    onSelectProviderType,
    onSearchProviderType,
}: StepOneProps) => {
    const { t } = useTranslation();
    const [subStr, setSubStr] = useState("");
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);

    const totalPages = Math.ceil(providerTypes.length / page_size);
    const pageItems = providerTypes.slice(currentPage * page_size, (currentPage + 1) * page_size);

    useEffect(() => {
        setCurrentPage(0);
    }, [providerTypes]);


    const goToPage = (page: number) => {
        if (page < 0 || page >= totalPages) return;
        setCurrentPage(page);
    };

    const handleCardSelect = (pt: ProviderType) => {
        onSelectProviderType(pt);
        setSelectedType(pt.id);
    };

    const SkeletonGrid = () => (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: page_size }).map((_, i) => (
                <div key={i} className="w-full h-[190px] p-4 flex flex-col items-center gap-2 rounded-2xl border-[3px] border-gray-lines bg-white overflow-hidden">
                    <div className={`w-[31px] h-[31px] rounded-full flex-shrink-0 ${skeletonClasses.base}`} />
                    <div className={`h-4 w-20 ${skeletonClasses.base}`} />
                    <div className={`h-3 w-24 ${skeletonClasses.base}`} />
                    <div className={`h-3 w-20 ${skeletonClasses.base}`} />
                    <div className={`h-3 w-16 ${skeletonClasses.base}`} />
                </div>
            ))}
        </div>
    );

    return (
        <div className="mt-8 w-full">
            <div className="flex flex-col mb-5">
                <label className={textClasses.subheading + " !text-[26px] text-center w-full"}>{t("What would you like to connect?")}</label>
                <div className="relative w-full mt-4">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.4351 10.0629H10.7124L10.4563 9.81589C11.3528 8.77301 11.8925 7.4191 11.8925 5.94625C11.8925 2.66209 9.23042 0 5.94625 0C2.66209 0 0 2.66209 0 5.94625C0 9.23042 2.66209 11.8925 5.94625 11.8925C7.4191 11.8925 8.77301 11.3528 9.81589 10.4563L10.0629 10.7124V11.4351L14.6369 16L16 14.6369L11.4351 10.0629ZM5.94625 10.0629C3.66838 10.0629 1.82962 8.22413 1.82962 5.94625C1.82962 3.66838 3.66838 1.82962 5.94625 1.82962C8.22413 1.82962 10.0629 3.66838 10.0629 5.94625C10.0629 8.22413 8.22413 10.0629 5.94625 10.0629Z" fill="#8F8F8F"/>
                        </svg>
                    </span>
                    <input
                        id="search"
                        placeholder={t("Search provider types")}
                        aria-label={t("Search provider types")}
                        value={subStr}
                        className={inputClasses.standard + " w-full pl-9"}
                        onChange={(e) => {
                            onSearchProviderType(e);
                            setSubStr(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="mb-6">
                {(reloadProviderTypes || loading) ? (
                    <SkeletonGrid />
                ) : (
                    <>
                        {providerTypes.length < 1 && !loading && fetchError && (
                            <NoResults
                                headerMsg={t("Could not load provider types")}
                                labelMsg={t("Please try again or contact system admin.")}
                            />
                        )}
                        {providerTypes.length < 1 && !loading && !fetchError && (
                            <NoResults
                                headerMsg={t("No Provider Types Found")}
                                labelMsg={t("You may need to contact system admin.")}
                            />
                        )}
                        {providerTypes.length >= 1 && (
                            <>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {pageItems.map((pt: ProviderType) => {
                                        const logo = getProviderLogo(pt.value);
                                        return <div
                                            key={pt.id || pt.value}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={pt.name}
                                            onClick={() => handleCardSelect(pt)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleCardSelect(pt);
                                                }
                                            }}
                                            className={`w-full h-[190px] p-4 flex flex-col items-center gap-2 rounded-2xl border-[3px] bg-white cursor-pointer overflow-hidden transition-colors duration-300 ${
                                                selectedType === pt.id && pt.id !== ""
                                                    ? "border-status-green"
                                                    : "border-gray-lines hover:border-status-green"
                                            }`}
                                        >
                                            {/* Circle is 31px; logo is 38px — intentional 1.2x zoom-crop via overflow:hidden */}
                                            <div className="w-[31px] h-[31px] flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center bg-pale-green text-off-black text-base font-bold leading-none">
                                                {logo
                                                    ? <img src={logo} alt={pt.name} className="w-[38px] h-[38px] object-cover" />
                                                    : pt.name.charAt(0).toUpperCase()
                                                }
                                            </div>
                                            <p className="font-medium text-off-black text-center line-clamp-2">{pt.name}</p>
                                            {pt.description && (
                                                <p className="text-sm text-secondary-gray text-center line-clamp-3">{pt.description}</p>
                                            )}
                                        </div>;
                                    })}
                                </div>
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-1 mt-4">
                                        <button
                                            type="button"
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 0}
                                            aria-label={t("Previous page")}
                                            className="px-2 py-1 text-medium-gray disabled:opacity-30 transition-colors duration-300"
                                        >
                                            &#8592;
                                        </button>
                                        {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                                            page === "..." ? (
                                                <span key={`ellipsis-${idx}`} aria-hidden="true" className="px-2 py-1 text-[15px] text-medium-gray">…</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    key={page}
                                                    onClick={() => goToPage(page as number)}
                                                    aria-current={currentPage === page ? "page" : undefined}
                                                    aria-label={`${t("Page")} ${(page as number) + 1}`}
                                                    className={`w-8 h-8 rounded-full text-[15px] text-medium-gray transition-colors duration-300 ${
                                                        currentPage === page
                                                            ? "border border-medium-gray"
                                                            : ""
                                                    }`}
                                                >
                                                    {(page as number) + 1}
                                                </button>
                                            )
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage >= totalPages - 1}
                                            aria-label={t("Next page")}
                                            className="px-2 py-1 text-medium-gray disabled:opacity-30 transition-colors duration-300"
                                        >
                                            &#8594;
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>

            <div className="py-8 px-8 bg-pale-green rounded-2xl transition-colors duration-300 mb-20">
                <WebhookApiPushModal
                    providerTypes={providerTypes}
                    selectedType={selectedType}
                    onSelectProviderType={onSelectProviderType}
                    setSelectedType={setSelectedType}
                />
            </div>
        </div>
    );
};

export default NewConnectionStepOne;
